import { Box, Typography, Button, useMediaQuery, useTheme } from "@mui/material";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useEffect, useState } from "react";
import Page from '../../components/Page'

const MotionBox = motion(Box);
const MotionTypography = motion(Typography);
const MotionButton = motion(Button);

// Composant pour les sections animées avec parallax
const AnimatedSection = ({ children, delay = 0, parallaxFactor = 0.5 }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.3 });
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (inView) {
      controls.start({ opacity: 1, y: 0 });
    }
  }, [controls, inView]);

  return (
    <MotionBox
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={controls}
      transition={{ duration: 0.8, delay }}
      sx={{
        width: "100%",
        transform: `translateY(${scrollY * parallaxFactor}px)`, // Ajout du parallax effect
      }}
    >
      {children}
    </MotionBox>
  );
};

export default function Services() {
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:600px)");
  const isDarkMode = theme.palette.mode === 'dark';
  const partners = ["LPM", "Le Méridien", "Pavillon de la Reine", "Kyriad"];
  const partners_logos = [
    "lpm.svg",
    "le-meridien.png",
    "pavillon.png",
    "kyriad.png",
  ];

  return (
    <Page title="Acceuil" sx={{mt:30, mb:10}}>
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        background: "linear-gradient(135deg, #D0AC37, #FF6B00)",
        textAlign: "center",
        px: 0,
        overflow: "hidden",
        position: "relative",
        overflowX: "hidden",
      }}
    >
      {/* Contenu */}
      {/* Section 1 : Hero */}
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          pt: { xs: 10, md: 12 },
        }}
      >
        <MotionBox
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <Box sx={{ mb: 4, width: isMobile ? 80 : 120 }}>
            <img alt="logo honest-inn" src="/statics/logo_white.png" />
          </Box>
        </MotionBox>

        <MotionTypography
          variant={isMobile ? "h4" : "h2"}
          gutterBottom
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          sx={{ fontWeight: "bold", color: "#FFF", maxWidth: "800px" }}
        >
          Recrutement repensé, opportunités illimitées
        </MotionTypography>

        <MotionTypography
          variant={isMobile ? "body1" : "h5"}
          gutterBottom
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
          sx={{ fontStyle: "italic", color: "#FFF", maxWidth: "600px" }}
        >
          Trouvez des talents exceptionnels dans l'hôtellerie et la restauration
          en quelques clics.
        </MotionTypography>

        <MotionButton
          href="/register"
          variant="contained"
          sx={{
            mt: 3,
            borderRadius: "30px",
            fontWeight: "bold",
            padding: isMobile ? "10px 20px" : "12px 24px",
            fontSize: isMobile ? "0.875rem" : "1rem",
            backgroundColor: "#FFF",
            color: "#FF6B00",
            "&:hover": { backgroundColor: "#f8f8f8" },
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Découvrir les opportunités
        </MotionButton>

        <Box
          sx={{
            mt: 5,
            display: "flex",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <motion.img
            src="/statics/home.svg"
            alt="Illustration accueil"
            width={isMobile ? "250" : "400"}
            height={isMobile ? "250" : "400"}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
          />
        </Box>
      </Box>

      {/* Section 2 : Chiffres clés */}
      <AnimatedSection delay={0.2} parallaxFactor={0.3}>
        <Box
          sx={{
            py: 10,
            backgroundColor: isDarkMode ? "#333" : "#FFF",
            marginBottom: "20px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            mx: "auto",
            width: { xs: "100%", sm: "80%" },
          }}
        >
          <Typography
            variant={isMobile ? "h5" : "h4"}
            sx={{ fontWeight: "bold", color: "#FF6B00", mb: 4 }}
          >
            Nos chiffres clés
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 4,
              justifyContent: "center",
            }}
          >
            <Box>
              <Typography
                variant="h3"
                sx={{ fontWeight: "bold", color: isDarkMode ? "#FFF" : "#333" }}
              >
                10 000+
              </Typography>
              <Typography variant="body1" sx={{ color: isDarkMode ? "#AAA" : "#666" }}>
                Candidats inscrits
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="h3"
                sx={{ fontWeight: "bold", color: isDarkMode ? "#FFF" : "#333" }}
              >
                500+
              </Typography>
              <Typography variant="body1" sx={{ color: isDarkMode ? "#AAA" : "#666" }}>
                Entreprises partenaires
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="h3"
                sx={{ fontWeight: "bold", color: isDarkMode ? "#FFF" : "#333" }}
              >
                95%
              </Typography>
              <Typography variant="body1" sx={{ color: isDarkMode ? "#AAA" : "#666" }}>
                Satisfaction clients
              </Typography>
            </Box>
          </Box>
        </Box>
      </AnimatedSection>

      {/* Section 3 : Partenaires */}
      <AnimatedSection delay={0.4} parallaxFactor={0.4}>
        <Box
          sx={{
            py: 10,
            backgroundColor: isDarkMode ? "#333" : "#FFF",
            marginBottom: "20px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            mx: "auto",
            width: { xs: "100%", sm: "80%" },
          }}
        >
          <Typography
            variant={isMobile ? "h5" : "h4"}
            sx={{
              fontWeight: "bold",
              color: "#FF6B00",
              mb: 4,
              marginBottom: "20px",
            }}
          >
            Ils nous font confiance
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 4,
              justifyContent: "center",
              width: { xs: "100%", md: "80%" },
            }}
          >
            {partners.map((partner, index) => (
              <Box
                key={index}
                sx={{
                  width: isMobile ? "40%" : "20%",
                  textAlign: "center",
                }}
              >
                <img
                  src={`/statics/partners/${partners_logos[index]}`}
                  alt={partner}
                  style={{ width: "100%", height: "auto" }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </AnimatedSection>

      {/* Section 4 : Avis clients */}
      <AnimatedSection delay={0.6} parallaxFactor={0.5}>
        <Box
          sx={{
            py: 10,
            backgroundColor: isDarkMode ? "#333" : "#FFF",
            marginBottom: "20px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            mx: "auto",
            width: { xs: "100%", sm: "80%" },
          }}
        >
          <Typography
            variant={isMobile ? "h5" : "h4"}
            sx={{ fontWeight: "bold", color: "#FF6B00", mb: 4 }}
          >
            Ce que disent nos clients
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 4,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {[
              {
                name: "Jean D.",
                comment: "Une plateforme intuitive et efficace !",
              },
              {
                name: "Marie L.",
                comment: "Le recrutement n’a jamais été aussi simple.",
              },
              {
                name: "Pierre T.",
                comment: "Des candidats de qualité, rapidement.",
              },
            ].map((testimonial, index) => (
              <Box
                key={index}
                sx={{
                  maxWidth: "300px",
                  textAlign: "center",
                  p: 2,
                  borderRadius: "10px",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                  backgroundColor: isDarkMode ? "#444" : "#FFF",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontStyle: "italic", color: isDarkMode ? "#AAA" : "#666" }}
                >
                  "{testimonial.comment}"
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mt: 2, fontWeight: "bold", color: isDarkMode ? "#FFF" : "#333" }}
                >
                  - {testimonial.name}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </AnimatedSection>

      {/* Section 5 : Call-to-action final */}
      <AnimatedSection delay={0.8} parallaxFactor={0.6}>
        <Box
          sx={{
            py: 10,
            backgroundColor: isDarkMode ? "#333" : "#FFF",
            marginBottom: "20px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            mx: "auto",
            width: { xs: "100%", sm: "80%" },
          }}
        >
          <Typography
            variant={isMobile ? "h5" : "h4"}
            sx={{ fontWeight: "bold", color: isDarkMode ? "#FFF" : "#000", mb: 2 }}
          >
            Prêt à transformer votre recrutement ?
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: isDarkMode ? "#AAA" : "#666" }}>
            Rejoignez-nous dès aujourd'hui et découvrez une nouvelle façon de
            recruter.
          </Typography>
          <MotionButton
            href="/register"
            variant="contained"
            sx={{
              borderRadius: "30px",
              fontWeight: "bold",
              padding: isMobile ? "10px 20px" : "12px 24px",
              fontSize: isMobile ? "0.875rem" : "1rem",
              backgroundColor: isDarkMode ? "#444" : "#FFF",
              color: isDarkMode ? "#FFF" : "#FF6B00",
              "&:hover": { backgroundColor: isDarkMode ? "#555" : "#f8f8f8" },
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Commencer maintenant
          </MotionButton>
        </Box>
      </AnimatedSection>
    </Box>
    </Page>
  );
}
